<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import AnalysisGroupEntityList from "../analysis/AnalysisGroupEntityList.vue";
import EntityAddRow from "../cards/EntityAddRow.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Facade } from "@/drawing";
import type { ObservationEntityRef } from "@/drawing/managers/ObservationsManager";
import type { MarkerPhotoState } from "@/utils/plan-analysis/types";

/**
 * Lien d'une observation avec le plan, dans sa carte — même mécanique que
 * la carte d'un groupe d'analyse : la liste de ses entités (un tap les
 * montre sur le plan, « Retirer » les détache), la ligne d'ajout d'éléments,
 * puis ce qui est propre aux observations : sur la ligne de chaque marqueur
 * photo, s'il a sa photo ou non ; « Tout détacher » et l'accès à la popup des
 * marqueurs.
 * L'AJOUT de photo ne passe pas par ici : il se fait via les cases d'attente
 * de la grille photos.
 */

defineProps<{
  /** Entités du plan rattachées à l'observation. */
  entities: ObservationEntityRef[];
  /** État photo de chaque marqueur, par handle. */
  photoStates: Map<string, MarkerPhotoState>;
  /** Nb d'éléments sélectionnés sur le plan et pas encore rattachés. */
  extraSelectedCount: number;
  /** Mode « cliquez pour ajouter » actif sur cette carte. */
  addMode: boolean;
}>();

const emit = defineEmits<{
  (e: "entity-remove", handle: string): void;
  (e: "add-selected"): void;
  (e: "toggle-add-mode"): void;
  (e: "detach-all"): void;
  (e: "open-marker-picker"): void;
}>();

const { t } = useTranslation();

/**
 * Montrer UNE entité : zoom ET sélection, pour que la bbox se recale sur
 * elle. Un simple zoom laisserait la grille de l'ancienne sélection à des
 * coordonnées écran périmées (bbox fantôme jusqu'au prochain changement de
 * vue).
 */
const focusEntity = (handle: string): void => {
  if (Facade.isInitialized) Facade.selection.highlightByHandles([handle]);
};

</script>

<template>
  <div class="observation-plan" @click.stop>
    <!-- Entités rattachées : un tap montre l'entité, « Retirer » la détache -->
    <AnalysisGroupEntityList
      v-if="entities.length"
      :entities="entities"
      :photo-states="photoStates"
      :remove-title="t('canvas:observations.card.removeEntity')"
      @select="focusEntity"
      @remove="emit('entity-remove', $event)"
    />
    <div v-else class="plan-label">
      {{ t("canvas:analysis.entityList.title", { count: 0 }) }}
    </div>

    <EntityAddRow
      :add-mode="addMode"
      :extra-selected-count="extraSelectedCount"
      @add-selected="emit('add-selected')"
      @toggle-add-mode="emit('toggle-add-mode')"
    />

    <!-- Actions : tout détacher (s'il y a des entités), marqueurs -->
    <div class="d-flex flex-wrap align-items-center gap-1 mt-2">
      <button
        v-if="entities.length"
        type="button"
        class="ghost-btn danger"
        @click="emit('detach-all')"
      >
        <PhosphorIcon name="x" :size="14" weight="bold" />
        {{ t("canvas:observations.editor.plan.detachAll") }}
      </button>
      <button
        type="button"
        class="ghost-btn"
        @click="emit('open-marker-picker')"
      >
        <PhosphorIcon name="magnifying-glass" :size="14" weight="bold" />
        {{ t("canvas:observations.markerPicker.open") }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Même libellé que les champs de la carte, quand la liste est vide.
.plan-label {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  opacity: 0.7;
  margin-bottom: 3px;
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  border-radius: var(--bs-border-radius);
  background-color: transparent;
  color: var(--card-accent-light, var(--bs-primary));
  font-size: 0.75rem;
  padding: 3px 7px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: color-mix(in srgb, var(--bs-primary) 15%, transparent);
    }
  }

  &.danger {
    color: #fca5a5;

    &:hover {
      background-color: color-mix(in srgb, #fca5a5 18%, transparent);
      color: #fecaca;
    }
  }
}
</style>
